import * as React from 'react';

export type PwaTransactionsBottomActions = {
  showPosted: boolean;
  isPostedActive: boolean;
  onActivity: () => void;
  onPosted: () => void;
};

type PwaTransactionsBottomActionsContextValue = {
  actions: PwaTransactionsBottomActions | null;
  setActions: (next: PwaTransactionsBottomActions | null) => void;
};

const PwaTransactionsBottomActionsContext =
  React.createContext<PwaTransactionsBottomActionsContextValue | null>(null);

export function PwaTransactionsBottomActionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [actions, setActions] =
    React.useState<PwaTransactionsBottomActions | null>(null);

  const value = React.useMemo(
    () => ({ actions, setActions }),
    [actions]
  );

  return (
    <PwaTransactionsBottomActionsContext.Provider value={value}>
      {children}
    </PwaTransactionsBottomActionsContext.Provider>
  );
}

export function usePwaTransactionsBottomActions(): PwaTransactionsBottomActions | null {
  return React.useContext(PwaTransactionsBottomActionsContext)?.actions ?? null;
}

/** Register Activity / Posted for the PWA bottom nav while the Transactions tab is mounted. */
export function useRegisterPwaTransactionsBottomActions(
  enabled: boolean,
  options: {
    showPosted: boolean;
    isPostedActive: boolean;
    onActivity: () => void;
    onPosted: () => void;
  }
): void {
  const setActions = React.useContext(
    PwaTransactionsBottomActionsContext
  )?.setActions;
  const { showPosted, isPostedActive, onActivity, onPosted } = options;

  React.useEffect(() => {
    if (!setActions) return undefined;
    if (!enabled) {
      setActions(null);
      return undefined;
    }
    setActions({
      showPosted,
      isPostedActive,
      onActivity,
      onPosted,
    });
    return () => {
      setActions(null);
    };
  }, [
    setActions,
    enabled,
    showPosted,
    isPostedActive,
    onActivity,
    onPosted,
  ]);
}
